// Appalachian State University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - App State's Common Data Set 2024-2025, the newest edition posted.
//    Departure: App State publishes its CDS only as an interactive Tableau
//    Public dashboard (Strategic Analytics and Institutional Research, CDS
//    Dashboard), not as a PDF, so the page cites the university's own
//    analytics.appstate.edu page that embeds it. The dashboard's Academic
//    Year menu ends at 2024-2025, so the counts are for Fall 2024, a year
//    older than most pages here. Read from it: C1 applicants, admits and
//    enrollees for Fall 2024 (the totals match IPEDS for Fall 2024; the
//    dashboard shows no residency table), C8 (written for Fall 2026
//    applicants), C21/C22 (no early decision; early action with dates but
//    no counts). Its cost section is for 2024-2025, so the cost comes from
//    the Office of Student Financial Aid's 2026-27 Undergraduate Estimated
//    Cost of Attendance (Boone campus, on campus). That table's column
//    header still says "2025-26"; the figures are the 2026-27 ones (the
//    2025-26 table on the archive page has different figures, and the
//    tuition matches the 2026-27 costs page).
//  - admissions.appstate.edu: First-year Student (rolling review, the
//    November 15 priority deadline, decision timing) and Application
//    Requirements (test policy); appstate.edu Undergraduate Admissions Dates
//    and Deadlines (scholarship offers, the May 1 deposit).
//  - College Scorecard API, school id 197869 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'App State Common Data Set 2024-2025 (CDS Dashboard)',
        url: 'https://analytics.appstate.edu/dash_cds_all_years',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'App State Admissions, First-year Student',
        url: 'https://admissions.appstate.edu/apply/first-year-student',
        read: '2026-10-01',
    },
    requirements: {
        label: 'App State Admissions, Application Requirements',
        url: 'https://admissions.appstate.edu/apply/application-requirements',
        read: '2026-10-01',
    },
    dates: {
        label: 'App State, Undergraduate Admissions Dates and Deadlines',
        url: 'https://www.appstate.edu/undergrad-deadlines/',
        read: '2026-10-01',
    },
    coa: {
        label: 'App State Student Financial Aid, 2026-27 Undergraduate Estimated Cost of Attendance',
        url: 'https://financialaid.appstate.edu/estimated-cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?197869-Appalachian-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'appalachian-state-university',
    kbId: 'appalachian_state_university',
    name: 'Appalachian State University',
    shortName: 'App State',
    aliases: ['App State', 'Appalachian State'],
    location: 'Boone, NC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (App State brand guide, colors); decoration only.
    branding: {
        primary: '#FFCC00',
        secondary: '#010101',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/appalachian_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 24614,
        admitted: 22188,
        enrolled: 4038,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'App State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'The 2024-2025 Common Data Set lists no early decision plan and a nonbinding early action plan (apply by November 1, notified by January 25), with no early action counts, so this page shows no early action rate. For fall 2027 entry, App State\'s admissions pages describe rolling review instead; see the deadlines below.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'November 15' },
        rows: [
            { plan: 'Rolling admission (priority deadline for admission and scholarships)', deadline: 'November 15', decision: '2 to 4 weeks after your application is complete', binding: false },
        ],
        note: 'App State reviews applications on a rolling basis and starts sending decisions in September. Complete your application, with all materials and the $65 fee, by November 15 for full consideration for admission and scholarships; applicants for the Chancellor\'s Scholarship and the Honors College also complete the extra scholarship prompt in the MyMountaineer Portal by then.',
        source: 'firstYear',
        binding: 'No App State plan is binding. Scholarship offers go out starting February 1, and admitted students secure a place with the enrollment deposit by May 1; deposits after that date are accepted if space is available.',
        bindingSource: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'App State is test-optional if your weighted GPA is 2.8 or higher on a 4.0 scale: you choose whether your SAT, ACT or CLT scores are considered, and you are not disadvantaged without them, for admission or scholarships. With a weighted GPA below 2.8, you need an ACT of 17 or higher or an SAT of 930 or higher. If you send scores, App State superscores them, and an official score report is needed once you confirm your enrollment.',
        source: 'requirements',
        cdsLine: 'The 2024-2025 Common Data Set, written for fall 2026 applicants, marks the SAT or ACT as required for some applicants and used for academic advising, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [8399, 27259] },
            { item: 'Housing (on campus)', amounts: [7578, 7578] },
            { item: 'Food', amounts: [5966, 5966] },
            { item: 'Books, course materials, supplies and equipment', amounts: [800, 800] },
            { item: 'Transportation', amounts: [1126, 1958] },
            { item: 'Personal and miscellaneous', amounts: [2138, 2138] },
            { item: 'Loan fees', amounts: [92, 92] },
        ],
        totals: [26099, 45791],
        note: 'The estimate for a full-time undergraduate (12 or more hours) living on campus at the Boone campus. In-state tuition and fees are the fixed tuition guarantee rate for an incoming North Carolina resident, and the textbook rental fee is included in tuition and fees. Tuition and fees can vary by program of study.',
        source: 'coa',
        netPrice: {
            average: 16082,
            byIncome: [
                { income: '$0 to $30,000', amount: 11070 },
                { income: '$30,001 to $48,000', amount: 10994 },
                { income: '$48,001 to $75,000', amount: 12977 },
                { income: '$75,001 to $110,000', amount: 19354 },
                { income: 'Over $110,000', amount: 22753 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from App State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
